import { Box, Divider, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { AwardLetterEvaluationDto, AwardLetterTypeDto, type SentStatutoryClausesDto } from "../../../autogen/bff-api";
import { visibleLength } from "../../../common/text/visibleLength";
import { AwardLetterScoreMatrix } from "./AwardLetterScoreMatrix";
import { relativeAdvantagesLabel } from "./relativeAdvantagesLabel";
import { clausesAsShown, forRecipient, letterIntro, signOff, StatutoryClauseContext } from "./awardLetterTemplates";

export type AwardLetterPreviewProps = {
  type: AwardLetterTypeDto;
  supplierName: string;
  winnerName: string;
  eventTitle: string;
  senderName?: string | null;
  signerName?: string | null;
  criterionJustifications: Record<string, string>;
  freeText?: string | null;
  rankingDepartureExplanation?: string | null;
  criteria: { id: string; name: string }[];
  evaluation?: AwardLetterEvaluationDto | null;
  /** When the letter was dispatched; the authoritative letter date. Falls back to today for buyer drafts. */
  sentAt?: string | null;
  clauseContext: StatutoryClauseContext;
  sentClauses?: SentStatutoryClausesDto | null;
  scoreHeading: string;
  forRejectedTender?: boolean;
  chosenTenderAdvantages?: string | null;
};

export const AwardLetterPreview = ({
  type,
  supplierName,
  winnerName,
  eventTitle,
  senderName,
  signerName,
  criterionJustifications,
  freeText,
  rankingDepartureExplanation,
  criteria,
  evaluation,
  sentAt,
  clauseContext,
  sentClauses,
  scoreHeading,
  forRejectedTender = false,
  chosenTenderAdvantages,
}: AwardLetterPreviewProps) => {
  const { t } = useTranslation();
  const isRejection = type === AwardLetterTypeDto.Rejection;
  const intro = letterIntro({ eventTitle, winnerName, senderName, clauseContext, isRejection, forRejectedTender });
  const clauses = clausesAsShown(forRecipient(clauseContext, isRejection ? winnerName : null), sentClauses);
  const justifiedCriteria = criteria.filter((c) => visibleLength(criterionJustifications[c.id] ?? "") > 0);
  const letterDate = (sentAt ? new Date(sentAt) : new Date()).toLocaleDateString("nb-NO", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <Box bg="smBackground" rounded="md" border="1px solid" borderColor="smBorder" p={{ base: "6", md: "10" }}>
      <Stack spacing="1" mb="6">
        <Text fontWeight="semibold" color="smText">
          {senderName || t("The contracting authority")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {letterDate}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("Reference")}: {eventTitle}
        </Text>
        <Text fontSize="sm" color="smMuted" mt="2">
          {t("To")}: {supplierName}
        </Text>
      </Stack>

      <Text fontWeight="semibold" fontSize="lg" color="smText" mb="4">
        {clauseContext.designContest
          ? t("Notification of the result of the design contest")
          : t("Notification of contract award")}
      </Text>

      <Text color="smText" whiteSpace="pre-wrap" mb="5">
        {intro}
      </Text>

      {evaluation && (
        <AwardLetterScoreMatrix
          evaluation={evaluation}
          criterionLabels={Object.fromEntries(criteria.map((c) => [c.id, c.name]))}
          recipientIsWinner={!isRejection}
          heading={scoreHeading}
        />
      )}

      {forRejectedTender && chosenTenderAdvantages?.trim() && (
        <Stack spacing="1" mb="5">
          <Text fontWeight="medium" color="smText">
            {relativeAdvantagesLabel(t, !!clauseContext.designContest)}
          </Text>
          <Text color="smText" whiteSpace="pre-wrap">
            {chosenTenderAdvantages}
          </Text>
        </Stack>
      )}

      {isRejection && rankingDepartureExplanation?.trim() && (
        <Stack spacing="1" mb="5">
          <Text fontWeight="medium" color="smText">
            {t("Why the contract is not awarded to your tender")}
          </Text>
          <Text color="smText" whiteSpace="pre-wrap">
            {rankingDepartureExplanation}
          </Text>
        </Stack>
      )}

      {isRejection && justifiedCriteria.length > 0 && (
        <Stack spacing="3" mb="5">
          <Text fontWeight="medium" color="smText">
            {t("Justification per criterion")}
          </Text>
          {justifiedCriteria.map((c) => (
            <Box key={c.id}>
              <Text fontWeight="medium" fontSize="sm" color="smText">
                {c.name}
              </Text>
              <Text color="smText" whiteSpace="pre-wrap">
                {criterionJustifications[c.id]}
              </Text>
            </Box>
          ))}
        </Stack>
      )}

      {freeText?.trim() && (
        <Text color="smText" whiteSpace="pre-wrap" mb="5">
          {freeText}
        </Text>
      )}

      {(clauses.standstill || clauses.complaint) && (
        <>
          <Divider my="5" />

          <Stack spacing="3" fontSize="sm" color="smMuted">
            {clauses.standstill && (
              <Box>
                <Text fontWeight="medium" color="smText">
                  {clauseContext.designContest ? t("No standstill period") : t("Standstill period")}
                </Text>
                <Text whiteSpace="pre-wrap">{clauses.standstill}</Text>
              </Box>
            )}
            {clauses.complaint && (
              <Box>
                <Text fontWeight="medium" color="smText">
                  {t("Right to complain")}
                </Text>
                <Text whiteSpace="pre-wrap">{clauses.complaint}</Text>
              </Box>
            )}
          </Stack>
        </>
      )}

      <Text color="smMuted" whiteSpace="pre-wrap" mt="6">
        {signOff(senderName, signerName)}
      </Text>
    </Box>
  );
};
